// File: frontend/src/pages/parent/ChildResults.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';

const ChildResults = () => {
  const [examsList, setExamsList] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch child examination results
   */
  const fetchChildResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/parent/results');
      if (response.data?.success) {
        const list = response.data.data || [];
        setExamsList(list);
        if (list.length > 0) {
          setSelectedExamId(String(list[0].exam_id));
        }
      } else {
        setError(response.data?.message || 'Failed to load examination results.');
      }
    } catch (err) {
      console.error('Error fetching child results:', err);
      setError(err.response?.data?.message || err.message || 'Error loading examination results.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChildResults();
  }, [fetchChildResults]);

  const selectedExam = examsList.find((e) => String(e.exam_id) === String(selectedExamId)) || examsList[0];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/parent/dashboard" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Child Examination Results</h1>
              <p className="text-xs text-slate-500">Subject wise marks breakdown & overall grades</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {loading ? (
          <div className="p-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-400 font-medium">
            Loading examination report cards...
          </div>
        ) : examsList.length === 0 ? (
          <div className="p-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-400 font-medium">
            No examination results recorded for your child yet.
          </div>
        ) : (
          <div className="space-y-6">
            
            {/* Exam Selector Tabs */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center gap-3 overflow-x-auto">
              <span className="text-xs font-bold text-slate-400 uppercase px-2">Select Exam:</span>
              {examsList.map((ex) => (
                <button
                  key={ex.exam_id}
                  onClick={() => setSelectedExamId(String(ex.exam_id))}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                    String(selectedExamId) === String(ex.exam_id)
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {ex.exam_name} ({ex.exam_term})
                </button>
              ))}
            </div>

            {selectedExam && (
              <div className="space-y-6">
                
                {/* Summary Card */}
                <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-lg grid grid-cols-1 sm:grid-cols-4 gap-6">
                  <div>
                    <span className="text-slate-400 text-xs uppercase block font-semibold">Total Obtained</span>
                    <span className="font-mono text-2xl font-extrabold">{selectedExam.total_obtained} / {selectedExam.total_max}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs uppercase block font-semibold">Percentage</span>
                    <span className="font-mono text-2xl font-extrabold text-blue-400">{selectedExam.percentage}%</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs uppercase block font-semibold">Overall Grade</span>
                    <span className="font-mono text-2xl font-extrabold text-emerald-400">{selectedExam.overall_grade}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs uppercase block font-semibold">Result Status</span>
                    <span className={`inline-block mt-1 text-sm font-extrabold px-3 py-0.5 rounded ${
                      selectedExam.percentage >= 40 ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                      {selectedExam.percentage >= 40 ? 'PASS' : 'FAIL'}
                    </span>
                  </div>
                </div>

                {/* Subject-wise Breakdown Table */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-6 border-b border-slate-100">
                    <h3 className="font-bold text-slate-900 text-base">{selectedExam.exam_name} Subject Breakdown</h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs font-sans">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                          <th className="py-3.5 px-6">Subject</th>
                          <th className="py-3.5 px-4 text-center">Code</th>
                          <th className="py-3.5 px-4 text-right">Max Marks</th>
                          <th className="py-3.5 px-4 text-right">Pass Marks</th>
                          <th className="py-3.5 px-4 text-right">Obtained Marks</th>
                          <th className="py-3.5 px-4 text-center">Grade</th>
                          <th className="py-3.5 px-4 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                        {selectedExam.subjects.map((sub) => {
                          const isSubjectPass = sub.marks_obtained >= sub.pass_marks;

                          return (
                            <tr key={sub.id} className="hover:bg-slate-50/80 transition">
                              <td className="py-4 px-6 font-bold text-slate-900 text-sm">
                                {sub.subject_name}
                              </td>
                              <td className="py-4 px-4 text-center font-mono text-slate-500">
                                {sub.subject_code}
                              </td>
                              <td className="py-4 px-4 text-right font-mono font-semibold">
                                {sub.total_marks}
                              </td>
                              <td className="py-4 px-4 text-right font-mono text-slate-500">
                                {sub.pass_marks}
                              </td>
                              <td className="py-4 px-4 text-right font-mono font-bold text-slate-900">
                                {sub.marks_obtained}
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-slate-800 border font-bold">
                                  {sub.grade}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                                  isSubjectPass ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-red-100 text-red-800 border border-red-300'
                                }`}>
                                  {isSubjectPass ? 'PASS' : 'FAIL'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                </div>

              </div>
            )}

          </div>
        )}

      </main>
    </div>
  );
};

export default ChildResults;
